<template>
  <div class="activity-carousel">
    <Carousel
      ref="carousel"
      auto-height
      navigate-by-slide
      :class="{
        'cancel-overflow': isFullscreen,
      }"
      :items-per-view="1"
      :speed="2500"
      :enable-buttons="false"
      @on-slide-change="handleSlideChange"
    >
      <button
        v-for="image in data"
        :key="image.id"
        class="activity-carousel__image"
      >
        <ActivityWsImage
          ref="image"
          :data="[{ value: image.value, id: Math.random() }]"
          :is-carousel-item="true"
          @on-fullscreen-toggle="onFullscreenToggle"
          @on-position-hotspots="positionHotspots"
        />
      </button>
    </Carousel>
    <div class="button-bar">
      <button
        :class="{
          'prev-button': true,
          disabled: !buttons.left,
        }"
        @click="prev"
      >
        <img
          v-tooltip="{
            content: $t('tooltip.activities.previous').toString(),
          }"
          class="prev-button__img arrow-left"
          src="/images/training-menu/arrow-green.png"
          alt="Prev Slider Icon"
          title="Previous slide"
        />
      </button>

      <button
        :class="{
          'next-button': true,
          disabled: !buttons.right,
        }"
        @click="next"
      >
        <img
          v-tooltip="{
            content: $t('tooltip.activities.next').toString(),
          }"
          class="next-button__img arrow-right"
          src="/images/training-menu/arrow-green.png"
          alt="Next Slider Icon"
          title="Next slide"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cancel-overflow {
  ::v-deep {
    .carousel {
      overflow: visible;
    }

    .carousel__track {
      transform: none !important;
    }
  }
}

.button-bar {
  position: relative;
  margin: 10px auto;
  display: flex;
  justify-content: space-between;
  width: 50%;
  @media (max-width: #{$tablet}px) {
    width: 60%;
  }
}
.arrow-left {
  transform: rotate(90deg);
}
.arrow-right {
  transform: rotate(-90deg);
}
.next-button,
.prev-button {
  height: 80px;
  width: 80px;
  border-radius: 40px;
  border-style: solid;
  border-width: 4px;
  border-color: #88c23f;
  background-color: white;
  box-shadow: 2px 2px 5px $grey400;
  margin: 0 20px;

  &__img {
    width: 60px;
    @media (max-width: #{$tablet}px) {
      width: 40px;
    }
  }

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  @media (max-width: #{$tablet}px) {
    height: 60px;
    width: 60px;
  }
}

.next-button {
  right: 32%;
  padding-left: 10px;

  @media (max-width: #{$tablet}px) {
    right: 20%;
  }
}

.prev-button {
  left: 32%;
  padding-right: 10px;

  @media (max-width: #{$tablet}px) {
    left: 20%;
  }
}

.activity-carousel {
  position: relative;
  margin: 0;
  width: 100%;
  height: auto;

  &__image {
    width: 100%;
    height: 100%;
  }
}
</style>

<script lang="ts">
import { Component, Prop, Watch, Ref, Vue } from 'vue-property-decorator';

import theme from '@/styles/theme';

import ActivityImage from '@/components/Activity/ActivityImage.vue';
import EventBus from '@/utils/helpers/EventBus';

import Carousel, { Carousel as Vue2SimpleCarousel } from 'vue2-simple-carousel';
import { IActivityComponentDataElement } from '@/models/interfaces/activities';
import ActivityWsImage from '@/components/Activity/ActivityWsImage.vue';

@Component({
  components: {
    ActivityWsImage,
    Carousel,
    ActivityImage,
  },
})
export default class ActivityWsImageCarousel extends Vue {
  @Prop({ required: true })
  private type!: string;

  @Prop({ required: true })
  private data!: any;

  @Ref('image')
  private images!: Vue[];

  @Ref('carousel')
  private carousel!: Vue & Vue2SimpleCarousel;

  private selectedSlide: null | IActivityComponentDataElement = null;
  private isFullscreen = false;

  private buttons = {
    left: false,
    right: true,
  };

  private next() {
    this.carousel.next();
  }

  private prev() {
    this.carousel.prev();
  }

  private positionHotspots(data: {
    width: number;
    height: number;
    top: number;
    position: string;
  }) {
    if (!this.images[0].$el) {
      return;
    }

    const borderSize = theme.activityMediaBorderSize.replace('px', '');
    EventBus.$emit('set-assets-hotspots-container-positioning', {
      ...data,
      left:
        this.images[0].$el.getBoundingClientRect().left +
        (Number(borderSize) || 0),
      controls: true,
    });
  }

  private handleSlideChange(index: number) {
    const maxIndex = this.data.length - 1;

    this.buttons = Object.assign(this.buttons, {
      left: index > 0,
      right: index < maxIndex,
    });

    const visibleIndex = index;
    const visibleSlide = this.data[visibleIndex];

    // Remove previous slide from solutions:
    if (this.selectedSlide !== null) {
      EventBus.$emit('activity-event-solution-del', {
        type: 'wsImageCarousel',
        data: this.selectedSlide,
      });
    }

    this.selectedSlide = { ...visibleSlide };

    if (this.type === 'wsImageCarouselHotspots') {
      return;
    }

    EventBus.$emit('activity-event-solution-add', {
      type: 'wsImageCarousel',
      data: this.selectedSlide,
    });
  }

  private onFullscreenToggle(val: boolean) {
    this.isFullscreen = val;
  }

  public mounted() {
    window.requestAnimationFrame(() => {
      this.handleSlideChange(0);
    });
  }

  @Watch('selectedSlide')
  private onSlideChange() {
    if (this.selectedSlide) {
      this.$nextTick(() => {
        EventBus.$emit(
          'activity-event-filter-hotspots-by-id',
          this.selectedSlide!.id,
        );
      });
    }
  }
}
</script>

<docs>

```jsx
<ActivityWsImageCarousel
  type="wsImageCarousel"
  :data="[
    { value: 'https://interactive-examples.mdn.mozilla.net/media/cc0-images/grapefruit-slice-332-332.jpg' },
    { value: 'https://interactive-examples.mdn.mozilla.net/media/cc0-images/grapefruit-slice-332-332.jpg' }
  ]"
/>
```
</docs>
